/**
 * Waveform colouring, ported from `spectral_color` in the Rust player's `app.rs`.
 *
 * The desktop app gates spectral colouring behind a Traktor analysis flag. That gate is
 * a product decision, not a technical one — the band energies are computed for every
 * track either way — and there is no Traktor dependency here, so every track gets it.
 */

export type Rgb = { r: number; g: number; b: number };

/** Dimming applied to the not-yet-played part of the waveform. */
export const UNPLAYED_SCALE = 0.45;

/**
 * Additive RGB from low/mid/high band energies: bass reds, mids greens, highs blues.
 * Each channel is the band's share of the dominant one, so a bass-heavy track still
 * shows colour variation instead of saturating to flat red.
 */
export function spectralColor(low: number, mid: number, high: number, played: boolean): Rgb {
  const dominant = Math.max(low, mid, high, 1e-4);
  const red = Math.pow(low / dominant, 0.7);
  const green = Math.pow(mid / dominant, 0.7);
  const blue = Math.pow(high / dominant, 0.7);
  const energy = Math.sqrt(Math.min(Math.max(dominant, 0), 1));
  const brightness = (0.35 + 0.65 * energy) * (played ? 1 : UNPLAYED_SCALE);
  const scale = 255 * brightness;
  return {
    r: Math.round(red * scale),
    g: Math.round(green * scale),
    b: Math.round(blue * scale),
  };
}

export function rgbToCss({ r, g, b }: Rgb): string {
  return `rgb(${r},${g},${b})`;
}

/**
 * Solid mode: a single played/unplayed pair, brightened by the column's own energy so
 * the waveform keeps its shape without the spectral hues.
 */
export function solidColor(amplitude: number, played: boolean): Rgb {
  const energy = Math.sqrt(Math.min(Math.max(amplitude, 0), 1));
  const brightness = (0.45 + 0.55 * energy) * (played ? 1 : UNPLAYED_SCALE);
  return played
    ? { r: Math.round(255 * brightness), g: Math.round(77 * brightness), b: Math.round(0) }
    : {
        r: Math.round(190 * brightness),
        g: Math.round(190 * brightness),
        b: Math.round(185 * brightness),
      };
}
